import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, ArrowLeft, AlertTriangle, Shield, Check, X, 
  Eye, MessageCircle, Clock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModerationPage = ({ user }) => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    fetchIncidents();
  }, [filter]);

  const fetchIncidents = async () => {
    try {
      const reviewed = filter === "reviewed" ? true : filter === "pending" ? false : undefined;
      const url = reviewed !== undefined 
        ? `${API}/moderation/incidents?reviewed=${reviewed}`
        : `${API}/moderation/incidents`;
      const response = await axios.get(url);
      setIncidents(response.data);
    } catch (error) {
      console.error("Error fetching incidents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (incidentId, action) => {
    try {
      await axios.post(`${API}/moderation/incidents/${incidentId}/review`, null, {
        params: { action, admin_email: user.email }
      });
      toast.success(`Incident ${action === 'warning' ? 'signalé' : 'ignoré'}`);
      fetchIncidents();
    } catch (error) {
      console.error("Error reviewing incident:", error);
      toast.error("Erreur lors du traitement");
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "bg-[#EF4444]";
      case "high": return "bg-[#F97316]";
      case "medium": return "bg-[#EAB308]";
      default: return "bg-[#64748B]";
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case "critical": return "Critique";
      case "high": return "Élevé";
      case "medium": return "Moyen";
      default: return "Faible";
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="moderation-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/director")}
            className="text-[#1A365D]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#EF4444] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Modération</span>
        </div>
        
        <Badge className="bg-[#EF4444]">{incidents.filter(i => !i.is_reviewed).length} à traiter</Badge>
      </nav>

      <main className="px-4 py-6 max-w-5xl mx-auto page-enter">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Incidents de Modération
          </h1>
          <p className="text-[#64748B]">
            Contenus signalés automatiquement pour comportement inapproprié
          </p>
        </div>

        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="pending" data-testid="filter-pending">
              À traiter
            </TabsTrigger>
            <TabsTrigger value="reviewed" data-testid="filter-reviewed">
              Traités
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-all">
              Tous
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Chargement...</div>
        ) : incidents.length === 0 ? (
          <Card className="card-sjh">
            <CardContent className="py-12 text-center">
              <Shield className="w-12 h-12 text-[#22C55E] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1A365D] mb-2">Aucun incident</h3>
              <p className="text-[#64748B]">
                {filter === "pending" 
                  ? "Tous les incidents ont été traités"
                  : "Aucun incident enregistré"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {incidents.map((incident, idx) => (
              <Card 
                key={idx} 
                className={`card-sjh ${!incident.is_reviewed ? 'border-l-4 border-l-[#EF4444]' : ''}`}
                data-testid={`incident-${idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getSeverityColor(incident.severity)}>
                          {getSeverityLabel(incident.severity)}
                        </Badge>
                        {incident.is_reviewed && (
                          <Badge variant="outline" className="border-[#22C55E] text-[#22C55E]">
                            Traité
                          </Badge>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <span className="font-medium text-[#1A365D]">
                          {incident.student_name || "Élève inconnu"}
                        </span>
                        <span className="text-[#64748B] text-sm ml-2">
                          {incident.class_name}
                        </span>
                      </div>
                      
                      <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 mb-3">
                        <p className="text-sm text-[#0F172A]">"{incident.message}"</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {incident.violations?.map((v, vidx) => (
                          <Badge key={vidx} variant="outline" className="text-xs border-[#EF4444] text-[#EF4444]">
                            {v}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-[#64748B]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(incident.timestamp).toLocaleString('fr-FR')}
                        </span>
                        {incident.is_reviewed && (
                          <span>
                            Traité par: {incident.reviewed_by}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!incident.is_reviewed && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
                          onClick={() => handleReview(incident.id, "warning")}
                          data-testid={`warning-${idx}`}
                        >
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Avertir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#64748B] text-[#64748B]"
                          onClick={() => handleReview(incident.id, "dismissed")}
                          data-testid={`dismiss-${idx}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Ignorer
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ModerationPage;
